
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { BarChart2 } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { useSupermarketPrices, useDeleteSupermarketPrice } from '@/hooks/use-supermarket-prices';
import { SupermarketPriceFilters, SupermarketPrice } from '@/types/database/supermarket-prices';
import SupermarketPricesHeader from '@/components/supermarket/SupermarketPricesHeader';
import SupermarketPricesFilter from '@/components/supermarket/SupermarketPricesFilter';
import SupermarketPricesTable from '@/components/supermarket/SupermarketPricesTable';
import SupermarketPriceForm from '@/components/supermarket/SupermarketPriceForm';
import SupermarketUpload from '@/components/supermarket/upload/SupermarketUpload';

const Supermarket: React.FC = () => {
  const [filters, setFilters] = useState<SupermarketPriceFilters>({});
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [editingPrice, setEditingPrice] = useState<SupermarketPrice | null>(null);

  const { data: prices = [], isLoading, refetch } = useSupermarketPrices(filters);
  const deleteSupermarketPrice = useDeleteSupermarketPrice();

  const handleFiltersChange = (newFilters: SupermarketPriceFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({});
  };

  const handleAddNew = () => {
    setEditingPrice(null);
    setShowAddDialog(true);
  };

  const handleUpload = () => {
    setShowUploadDialog(true);
  };

  const handleEdit = (price: SupermarketPrice) => {
    setEditingPrice(price);
    setShowAddDialog(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this supermarket price?')) {
      await deleteSupermarketPrice.mutateAsync(id);
    }
  };

  const handleFormSuccess = () => {
    setShowAddDialog(false);
    setEditingPrice(null);
    refetch();
  };

  const handleUploadSuccess = () => {
    setShowUploadDialog(false);
    refetch();
  };

  return (
    <Layout>
      <div className="container mx-auto p-6">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <BarChart2 className="h-8 w-8 text-red-600" />
            <h1 className="text-3xl font-bold text-gray-900">Supermarket Prices</h1>
          </div>
          <p className="text-gray-600">
            Supermarket retail price monitoring
          </p>
        </div>

        <div className="space-y-6">
          <SupermarketPricesHeader
            onAddNew={handleAddNew}
            onUpload={handleUpload}
          />

          <SupermarketPricesFilter
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onClearFilters={handleClearFilters}
          />

          <Card>
            <CardHeader>
              <CardTitle>Supermarket Prices</CardTitle>
            </CardHeader>
            <CardContent>
              <SupermarketPricesTable
                prices={prices}
                onEdit={handleEdit}
                onDelete={handleDelete}
                isLoading={isLoading}
              />
            </CardContent>
          </Card>
        </div>

        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingPrice ? 'Edit Supermarket Price' : 'Add New Supermarket Price'}
              </DialogTitle>
            </DialogHeader>
            <SupermarketPriceForm
              initialData={editingPrice}
              onSuccess={handleFormSuccess}
              onCancel={() => setShowAddDialog(false)}
            />
          </DialogContent>
        </Dialog>

        {showUploadDialog && (
          <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Upload Supermarket Prices</DialogTitle>
              </DialogHeader>
              <SupermarketUpload onSuccess={handleUploadSuccess} />
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Layout>
  );
};

export default Supermarket;
